import { useCallback, useEffect, useState } from 'react';

import type { ProjectSlackConfig, TestMessageResponse } from '@/api/slack-config';
import { getSlackConfig, saveSlackConfig, sendTestMessage } from '@/api/slack-config';
import { ApiError } from '@/modules/api/api-client';

// --- Constants ---

const PR_EVENT_TYPES = [
  { value: 'pr.created', label: 'PR Created' },
  { value: 'pr.approved', label: 'PR Approved' },
  { value: 'pr.merged', label: 'PR Merged' },
  { value: 'pr.review_requested', label: 'Review Requested' },
] as const;

const DEPLOY_EVENT_TYPES = [
  { value: 'deployment.started', label: 'Deployment Started' },
  { value: 'deployment.completed', label: 'Deployment Completed' },
  { value: 'deployment.failed', label: 'Deployment Failed' },
] as const;

// --- Types ---

interface TestStatus {
  type: 'success' | 'error';
  message: string;
}

// --- Sub-components ---

function LoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="h-8 w-48 rounded-lg bg-gray-200 dark:bg-gray-700" />
      <div className="h-10 rounded-lg bg-gray-200 dark:bg-gray-700" />
      <div className="h-32 rounded-lg bg-gray-200 dark:bg-gray-700" />
      <div className="h-32 rounded-lg bg-gray-200 dark:bg-gray-700" />
    </div>
  );
}

function Toggle({
  checked,
  onChange,
  id,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  id: string;
  label: string;
}) {
  return (
    <label htmlFor={id} className="inline-flex items-center gap-2 cursor-pointer">
      <span className="text-sm font-medium text-content">{label}</span>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 ${
          checked ? 'bg-brand-700' : 'bg-gray-300 dark:bg-gray-600'
        }`}
      >
        <span
          className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
            checked ? 'translate-x-4.5' : 'translate-x-0.5'
          }`}
        />
      </button>
    </label>
  );
}

function EventSection({
  title,
  webhookUrl,
  onWebhookUrlChange,
  eventTypes,
  selectedEvents,
  onEventToggle,
  testStatus,
  testLoading,
  onSendTest,
  disabled,
}: {
  title: string;
  webhookUrl: string;
  onWebhookUrlChange: (url: string) => void;
  eventTypes: ReadonlyArray<{ value: string; label: string }>;
  selectedEvents: string[];
  onEventToggle: (event: string, checked: boolean) => void;
  testStatus: TestStatus | null;
  testLoading: boolean;
  onSendTest: () => void;
  disabled: boolean;
}) {
  const sectionId = title.toLowerCase().replace(/\s+/g, '-');
  const webhookInputId = `${sectionId}-webhook-url`;

  return (
    <fieldset
      className={`rounded-lg border border-border p-4 space-y-3 ${disabled ? 'opacity-60' : ''}`}
      disabled={disabled}
    >
      <legend className="text-sm font-semibold text-content px-1">{title}</legend>

      <div>
        <label htmlFor={webhookInputId} className="block text-xs font-medium text-content-secondary mb-1">
          Webhook URL
        </label>
        <div className="flex gap-2">
          <input
            id={webhookInputId}
            type="url"
            value={webhookUrl}
            onChange={(e) => onWebhookUrlChange(e.target.value)}
            placeholder="https://hooks.slack.com/services/..."
            className="flex-1 rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-describedby={testStatus ? `${sectionId}-test-status` : undefined}
          />
          <button
            type="button"
            onClick={onSendTest}
            disabled={!webhookUrl.trim() || testLoading}
            className="shrink-0 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-content-secondary hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {testLoading ? 'Sending...' : 'Send Test Message'}
          </button>
        </div>
        {testStatus && (
          <p
            id={`${sectionId}-test-status`}
            role="status"
            className={`mt-1 text-xs ${
              testStatus.type === 'success'
                ? 'text-green-700 dark:text-green-400'
                : 'text-red-700 dark:text-red-400'
            }`}
          >
            {testStatus.message}
          </p>
        )}
      </div>

      <div>
        <span className="block text-xs font-medium text-content-secondary mb-2">Event Types</span>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {eventTypes.map((evt) => (
            <label key={evt.value} className="inline-flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedEvents.includes(evt.value)}
                onChange={(e) => onEventToggle(evt.value, e.target.checked)}
                className="rounded border-border text-brand-700 focus:ring-brand-600"
              />
              <span className="text-xs text-content">{evt.label}</span>
            </label>
          ))}
        </div>
      </div>
    </fieldset>
  );
}

// --- Main Component ---

interface SlackIntegrationProps {
  projectKey: string;
}

export function SlackIntegration({ projectKey }: SlackIntegrationProps) {
  // --- State ---
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [enabled, setEnabled] = useState(false);
  const [prWebhookUrl, setPrWebhookUrl] = useState('');
  const [deployWebhookUrl, setDeployWebhookUrl] = useState('');
  const [prEventTypes, setPrEventTypes] = useState<string[]>([]);
  const [deployEventTypes, setDeployEventTypes] = useState<string[]>([]);

  const [prTestStatus, setPrTestStatus] = useState<TestStatus | null>(null);
  const [prTestLoading, setPrTestLoading] = useState(false);
  const [deployTestStatus, setDeployTestStatus] = useState<TestStatus | null>(null);
  const [deployTestLoading, setDeployTestLoading] = useState(false);

  // --- Load config ---
  const loadConfig = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const config = await getSlackConfig(projectKey);
      setEnabled(config.enabled);
      setPrWebhookUrl(config.prEventsWebhookUrl ?? '');
      setDeployWebhookUrl(config.deployEventsWebhookUrl ?? '');
      setPrEventTypes(config.prEventTypes);
      setDeployEventTypes(config.deployEventTypes);
    } catch (err) {
      if (err instanceof ApiError) {
        setLoadError(`Failed to load Slack configuration: ${err.message}`);
      } else {
        setLoadError('Unable to connect to server. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [projectKey]);

  useEffect(() => {
    if (projectKey) {
      loadConfig();
    }
  }, [projectKey, loadConfig]);

  // --- Save config ---
  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const config: ProjectSlackConfig = {
        projectKey,
        prEventsWebhookUrl: prWebhookUrl.trim() || null,
        deployEventsWebhookUrl: deployWebhookUrl.trim() || null,
        prEventTypes,
        deployEventTypes,
        enabled,
      };
      await saveSlackConfig(projectKey, config);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { message?: string } | undefined;
        setSaveError(body?.message ?? err.message);
      } else {
        setSaveError('Unable to connect to server. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  // --- Test message handlers ---
  const handlePrTest = async () => {
    setPrTestLoading(true);
    setPrTestStatus(null);
    try {
      const result: TestMessageResponse = await sendTestMessage(projectKey, prWebhookUrl.trim());
      if (result.success) {
        setPrTestStatus({ type: 'success', message: '✓ Message sent successfully' });
      } else {
        setPrTestStatus({ type: 'error', message: `Test failed: ${result.error ?? 'Unknown error'}` });
      }
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { message?: string } | undefined;
        setPrTestStatus({ type: 'error', message: `Test failed: ${body?.message ?? err.message}` });
      } else {
        setPrTestStatus({ type: 'error', message: 'Test failed: Unable to connect to server.' });
      }
    } finally {
      setPrTestLoading(false);
    }
  };

  const handleDeployTest = async () => {
    setDeployTestLoading(true);
    setDeployTestStatus(null);
    try {
      const result: TestMessageResponse = await sendTestMessage(projectKey, deployWebhookUrl.trim());
      if (result.success) {
        setDeployTestStatus({ type: 'success', message: '✓ Message sent successfully' });
      } else {
        setDeployTestStatus({ type: 'error', message: `Test failed: ${result.error ?? 'Unknown error'}` });
      }
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { message?: string } | undefined;
        setDeployTestStatus({ type: 'error', message: `Test failed: ${body?.message ?? err.message}` });
      } else {
        setDeployTestStatus({ type: 'error', message: 'Test failed: Unable to connect to server.' });
      }
    } finally {
      setDeployTestLoading(false);
    }
  };

  // --- Event type toggle handlers ---
  const handlePrEventToggle = (event: string, checked: boolean) => {
    setPrEventTypes((prev) =>
      checked ? [...prev, event] : prev.filter((e) => e !== event)
    );
  };

  const handleDeployEventToggle = (event: string, checked: boolean) => {
    setDeployEventTypes((prev) =>
      checked ? [...prev, event] : prev.filter((e) => e !== event)
    );
  };

  // --- Render: Load error state ---
  if (loadError && !loading) {
    return (
      <section className="mt-6">
        <h3 className="text-sm font-semibold text-content mb-3">Slack Integration</h3>
        <div role="alert" className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
          <p className="text-sm text-red-700 dark:text-red-300">{loadError}</p>
          <button
            type="button"
            onClick={loadConfig}
            className="mt-2 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-content-secondary hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2"
          >
            Retry
          </button>
        </div>
      </section>
    );
  }

  // --- Render: Loading state ---
  if (loading) {
    return (
      <section className="mt-6">
        <h3 className="text-sm font-semibold text-content mb-3">Slack Integration</h3>
        <LoadingSkeleton />
      </section>
    );
  }

  // --- Render: Main form ---
  return (
    <section className="mt-6 space-y-4">
      {/* Header with master toggle */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-content">Slack Integration</h3>
        <Toggle
          id="slack-master-toggle"
          checked={enabled}
          onChange={setEnabled}
          label={enabled ? 'Enabled' : 'Disabled'}
        />
      </div>

      {/* Paused banner */}
      {!enabled && (
        <div
          role="status"
          className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3"
        >
          <p className="text-xs text-amber-700 dark:text-amber-300 font-medium">
            Slack notifications are paused. Enable the integration to resume delivery.
          </p>
        </div>
      )}

      {/* Save error banner */}
      {saveError && (
        <div role="alert" className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
          <p className="text-sm text-red-700 dark:text-red-300">{saveError}</p>
        </div>
      )}

      {/* PR Events Section */}
      <EventSection
        title="PR Events"
        webhookUrl={prWebhookUrl}
        onWebhookUrlChange={setPrWebhookUrl}
        eventTypes={PR_EVENT_TYPES}
        selectedEvents={prEventTypes}
        onEventToggle={handlePrEventToggle}
        testStatus={prTestStatus}
        testLoading={prTestLoading}
        onSendTest={handlePrTest}
        disabled={!enabled}
      />

      {/* Deploy Events Section */}
      <EventSection
        title="Deploy Events"
        webhookUrl={deployWebhookUrl}
        onWebhookUrlChange={setDeployWebhookUrl}
        eventTypes={DEPLOY_EVENT_TYPES}
        selectedEvents={deployEventTypes}
        onEventToggle={handleDeployEventToggle}
        testStatus={deployTestStatus}
        testLoading={deployTestLoading}
        onSendTest={handleDeployTest}
        disabled={!enabled}
      />

      {/* Save button */}
      <div className="pt-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </section>
  );
}
